import { resolveAppLanguage } from '../../../i18n/language';

/** Shown when site settings have no name (matches document title fallback). */
export const DEFAULT_SITE_DISPLAY_NAME = '';

/**
 * @param {unknown} value
 * @returns {string}
 */
function trimName(value) {
  if (value == null) return '';
  return String(value).trim();
}

/**
 * First non-empty name in English → Arabic → Chinese order.
 * @param {string} en
 * @param {string} ar
 * @param {string} zh
 * @returns {string}
 */
function firstAvailableName(en, ar, zh) {
  return en || ar || zh || DEFAULT_SITE_DISPLAY_NAME;
}

/**
 * Localized website display name from site settings.
 * Prefers the name for the active language when set; otherwise uses whatever exists
 * in order: English → Arabic → Chinese.
 *
 * @param {{ site_name?: string, site_name_ar?: string, site_name_zh?: string } | null | undefined} settings
 * @param {string} [i18nLanguage]
 * @returns {string}
 */
export function getSiteDisplayName(settings, i18nLanguage) {
  if (!settings) return DEFAULT_SITE_DISPLAY_NAME;
  const en = trimName(settings.site_name);
  const ar = trimName(settings.site_name_ar);
  const zh = trimName(settings.site_name_zh);
  const lang = resolveAppLanguage(i18nLanguage);

  if (lang === 'ar' && ar) return ar;
  if (lang === 'zh' && zh) return zh;
  if (lang === 'en' && en) return en;

  return firstAvailableName(en, ar, zh);
}

/**
 * Site name on public payment pages (/pay/i/, /pay/result).
 * When UI is Chinese, keep the English brand name (not site_name_zh).
 *
 * @param {{ site_name?: string, site_name_ar?: string, site_name_zh?: string } | null | undefined} settings
 * @param {string} [i18nLanguage]
 * @returns {string}
 */
export function getPublicPaySiteDisplayName(settings, i18nLanguage) {
  const lang = resolveAppLanguage(i18nLanguage);
  if (lang === 'zh') {
    return getSiteDisplayName(settings, 'en');
  }
  return getSiteDisplayName(settings, i18nLanguage);
}

/**
 * Permission records from the API often use a leading "Gofar" brand in display/name strings.
 * Replace that token with the configured site name for UI consistency.
 *
 * @param {string} label
 * @param {string} siteDisplayName
 * @returns {string}
 */
export function formatPermissionLabelForSite(label, siteDisplayName) {
  if (label == null || typeof label !== 'string') return label;
  const site = (siteDisplayName && String(siteDisplayName).trim()) || DEFAULT_SITE_DISPLAY_NAME;
  return label.replace(/^gofar\b/i, site);
}
